import React, { type MutableRefObject, useEffect, useMemo } from 'react';

import {
  ChevronDownIcon,
  ChevronUpIcon,
  DatePicker,
  Divider,
  Input,
  RefreshIcon,
  Select,
  Text,
  TextButton,
  type TypeSelectOption,
} from '@thefarmersfront/kitchen';

import { useShallow } from 'zustand/react/shallow';

import { FormField, InfoText, Table } from '~/components/common';
import { ReceivePickupInfoForm, renderOperationTimesDropdown, renderOperationTimesSelect } from '~/components/receive';

import { useToggle } from '~/hooks/common';
import { useGetFilterAllQuery, useOperationDateTime, usePickupAddressHandler } from '~/hooks/receive';
import { DEFAULT_RECEIVE_REQUEST_ORDER_INFO_FILTER_ERROR_STATUS, useReceiveStore, useUserInfoStore } from '~/store';
import { formatDate, formatNumber, getReceiveRegisterOrderInfoColumnDefs } from '~/utils';

import { DATE_FORMAT, DEFAULT_INPUT_STATUS, PICKUP_SHIPPING_TYPES, RECEIVE_TOOLTIP_MESSAGE } from '~/constants';

import type {
  TypeReceiveRequestOrder,
  TypeReceiveRequestOrderInfoFilter,
  TypeReceiveRequestOrderInfoFilterErrorState,
} from '~/types/receive.types';

import styles from './ReceiveRegisterOrderInfo.module.scss';

/** 주문 상품 표 최대 높이. 넘치면 표 안에서 스크롤한다. */
const ORDER_ITEMS_TABLE_MAX_HEIGHT = 396;

type TypeReceiveRegisterOrderInfoProps = {
  orderData: TypeReceiveRequestOrder;
  totalOrderCount: number;
  index: number;
  supplierCode: string;
  errorFieldRefs: MutableRefObject<Record<string, HTMLElement | null>>;
};

/**
 * [입고관리 > 입고요청관리] 입고요청등록 모달 > STEP 2 > 입고 예정 정보 컴포넌트
 */
const ReceiveRegisterOrderInfo = ({
  orderData,
  totalOrderCount,
  index,
  supplierCode,
  errorFieldRefs,
}: TypeReceiveRegisterOrderInfoProps) => {
  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermissionNFA = hasPermission([{ ownerType: 'NFA' }]);

  const { columnDefs } = useMemo(() => getReceiveRegisterOrderInfoColumnDefs({ hasPermissionNFA }), [hasPermissionNFA]);

  const { data: filterOptions } = useGetFilterAllQuery('ADD_RECEIVING_GOODS');

  const [
    requestModalOrderInfoValue,
    setRequestModalOrderInfoValue,
    requestModalOrderInfoStatus,
    setRequestModalOrderInfoStatus,
  ] = useReceiveStore(
    useShallow((state) => [
      state.requestModalOrderInfoValue,
      state.setRequestModalOrderInfoValue,
      state.requestModalOrderInfoStatus,
      state.setRequestModalOrderInfoStatus,
    ])
  );

  const orderFilter = requestModalOrderInfoValue.find((filter) => filter.orderId === orderData.id) ?? {
    orderId: orderData.id,
    releaseCode: null,
    courier: '',
    invoiceNumber: '',
    expectedDate: null,
    expectedTime: null,
    kurlyPickupAddress: 'default',
    milkrunPickupAddresses: {
      default: true,
      etc1: false,
      etc2: false,
    },
    pickupAddressData: {},
  };
  const isReleaseCodeParcel = orderFilter.releaseCode?.value === 'PARCEL';
  const isReleaseCodeKurlyPickup = orderFilter.releaseCode?.value === PICKUP_SHIPPING_TYPES.KURLY_PICKUP;
  const isReleaseCodeMilkrunPickup = orderFilter.releaseCode?.value === PICKUP_SHIPPING_TYPES.MILKRUN_PICKUP;
  const isReleaseCodePickup = isReleaseCodeKurlyPickup || isReleaseCodeMilkrunPickup;

  const setOrderFilter = (
    newFilter:
      | TypeReceiveRequestOrderInfoFilter
      | ((prev: TypeReceiveRequestOrderInfoFilter) => TypeReceiveRequestOrderInfoFilter)
  ) => {
    const newFilters = [...requestModalOrderInfoValue];
    const index = newFilters.findIndex((filter) => filter.orderId === orderData.id);

    newFilters[index] = typeof newFilter === 'function' ? newFilter(newFilters[index]) : newFilter;

    setRequestModalOrderInfoValue(newFilters);
  };

  const orderFilterState = requestModalOrderInfoStatus.find((filter) => filter.orderId === orderData.id) ?? {
    orderId: orderData.id,
    ...DEFAULT_RECEIVE_REQUEST_ORDER_INFO_FILTER_ERROR_STATUS,
  };
  const setOrderFilterState = (
    newState:
      | TypeReceiveRequestOrderInfoFilterErrorState
      | ((prev: TypeReceiveRequestOrderInfoFilterErrorState) => TypeReceiveRequestOrderInfoFilterErrorState)
  ) => {
    const newStates = [...requestModalOrderInfoStatus];
    const index = newStates.findIndex((filter) => filter.orderId === orderData.id);

    newStates[index] = typeof newState === 'function' ? newState(newStates[index]) : newState;

    setRequestModalOrderInfoStatus(newStates);
  };

  const { isToggle: isItemListOpen, handleToggle: handleToggleItemList } = useToggle();

  // 입고예정일 관리 훅 사용
  const {
    operationDates,
    operationTimes: optionOperationTime,
    lastFetchedAt: lastFetchedOperationTimesTimestamp,
    handleDateChange: handleOperationDateChange,
    handleTimeChange: handleOperationTimeChange,
    refresh: refreshOperationDateTimes,
  } = useOperationDateTime({
    orderId: orderData.id,
    releaseCode: orderFilter.releaseCode,
    expectedDate: orderFilter.expectedDate,
    expectedTime: orderFilter.expectedTime,
    isParcel: isReleaseCodeParcel,
    onUpdate: (updates) => {
      const updatedFilter: Partial<TypeReceiveRequestOrderInfoFilter> = {};

      if (updates.expectedDate !== undefined) {
        updatedFilter.expectedDate = updates.expectedDate;
      }

      if (updates.expectedTime !== undefined) {
        updatedFilter.expectedTime = updates.expectedTime as TypeSelectOption<string> | null;
      }

      setOrderFilter((prev) => ({ ...prev, ...updatedFilter }));
    },
    onValidationChange: (state) => {
      setOrderFilterState((prev) => ({ ...prev, ...state }));
    },
  });

  useEffect(() => {
    if (orderFilterState.expectedDateTime?.status === 'error') {
      refreshOperationDateTimes();
    }
  }, [orderFilterState.expectedDateTime?.status]);

  // 픽업 출고지 관리 Custom Hook
  const { handlePickupAddressDataChange, requestDefaultPickupAddress } = usePickupAddressHandler({
    supplierCode,
    currentData: orderFilter,
    onDataChange: setOrderFilter,
  });

  const handleChangeReleaseCode = (
    _: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    option: TypeSelectOption<string>
  ) => {
    setOrderFilter((prev) => {
      const updatedOrderFilter = {
        ...prev,
        releaseCode: option,
        courier: '',
        invoiceNumber: '',
        expectedDate: null,
        expectedTime: null,
      };

      if (
        option.value === PICKUP_SHIPPING_TYPES.KURLY_PICKUP ||
        option.value === PICKUP_SHIPPING_TYPES.MILKRUN_PICKUP
      ) {
        requestDefaultPickupAddress(option.value, updatedOrderFilter);
        return prev;
      }

      return updatedOrderFilter;
    });

    setOrderFilterState((prev) => ({
      ...prev,
      releaseCode: { status: 'default' },
      expectedDate: { status: 'default' },
      expectedTime: { status: 'default' },
      pickupAddressData: DEFAULT_RECEIVE_REQUEST_ORDER_INFO_FILTER_ERROR_STATUS.pickupAddressData,
    }));
  };

  return (
    <div key={orderData.id} className={styles['order-info-container']}>
      <Text size="xxlarge" weight="bold" element="p">
        입고 예정 정보{totalOrderCount > 1 ? ` ${index + 1}` : ''}
      </Text>
      <div>
        <FormField
          label="입고지"
          contents={
            <Text size="large" element="span">
              {orderData.inboundAreaAddress}
            </Text>
          }
          contentsClassName={`${styles['info']} ${styles['address']}`}
        />
        <FormField
          label="입고 상품"
          contents={
            <>
              <TextButton
                size="small"
                weight="regular"
                rightIcon={
                  isItemListOpen ? (
                    <ChevronUpIcon size={16} color="#672091" />
                  ) : (
                    <ChevronDownIcon size={16} color="#672091" />
                  )
                }
                onClick={handleToggleItemList}
              >
                {orderData.items[0].goodsName}
                {orderData.skuCount > 1 ? ` 외 ${formatNumber(orderData.skuCount - 1)}종` : ''}
                {` 총 ${formatNumber(orderData.unitSum)}개`}
              </TextButton>
              {isItemListOpen && (
                <Table
                  className={styles['order-items-table']}
                  data={orderData.items}
                  columns={columnDefs}
                  autoHeight
                  maxHeight={ORDER_ITEMS_TABLE_MAX_HEIGHT}
                />
              )}
            </>
          }
          contentsClassName={`${styles['info']} ${styles['goods-name']}`}
        />
      </div>
      <Divider orientation="horizontal" color="gray100" />
      {isReleaseCodePickup && <Divider orientation="horizontal" color="gray100" />}
      <FormField
        ref={(el) => {
          if (el) errorFieldRefs.current[`${orderData.id}.releaseCode`] = el;
        }}
        label="입고 방법"
        required
        tooltip={{
          description: RECEIVE_TOOLTIP_MESSAGE.RELEASE_CODE_PICKUP_EXPECTED_DATE,
          maxWidth: '226px',
        }}
        contents={
          <Select
            size="small"
            placeholder="선택"
            maxWidth="229px"
            dropdownPortalTarget={document.body}
            shouldCloseOnNotInView
            status={orderFilterState.releaseCode.status}
            validationMessage={orderFilterState.releaseCode.message}
            value={orderFilter.releaseCode}
            options={filterOptions?.releaseCode ?? []}
            onChange={handleChangeReleaseCode}
            onClick={() => setOrderFilterState((prev) => ({ ...prev, releaseCode: { status: 'default' } }))}
          />
        }
        contentsClassName={styles['info']}
      />
      {isReleaseCodePickup && (
        <ReceivePickupInfoForm
          pickupType={isReleaseCodeKurlyPickup ? 'kurly' : 'milkrun'}
          data={{
            kurlyPickupAddress: orderFilter.kurlyPickupAddress,
            milkrunPickupAddresses: orderFilter.milkrunPickupAddresses,
            pickupAddressData: orderFilter.pickupAddressData ?? {},
          }}
          dataState={{
            kurlyPickupAddress: orderFilterState.kurlyPickupAddress,
            milkrunPickupAddresses: orderFilterState.milkrunPickupAddresses,
            pickupAddressData: orderFilterState.pickupAddressData,
          }}
          onKurlyPickupAddressChange={(value) => {
            setOrderFilter((prev) => ({
              ...prev,
              kurlyPickupAddress: value,
            }));
            setOrderFilterState((prev) => ({ ...prev, kurlyPickupAddress: DEFAULT_INPUT_STATUS }));
          }}
          onMilkrunPickupAddressChange={(addressType, checked) => {
            setOrderFilter((prev) => ({
              ...prev,
              milkrunPickupAddresses: {
                ...prev.milkrunPickupAddresses,
                [addressType]: checked,
              },
            }));
            setOrderFilterState((prev) => ({ ...prev, milkrunPickupAddresses: DEFAULT_INPUT_STATUS }));
          }}
          onPickupAddressDataChange={handlePickupAddressDataChange}
          onResetPickupAddressDataState={(addressType, key) => {
            setOrderFilterState((prev) => ({
              ...prev,
              pickupAddressData: {
                ...prev.pickupAddressData,
                [addressType]: { ...prev.pickupAddressData?.[addressType], [key]: DEFAULT_INPUT_STATUS },
              },
            }));
          }}
          errorFieldRefs={errorFieldRefs}
          orderId={orderData.id}
        />
      )}
      {isReleaseCodeParcel && (
        <>
          <FormField
            label="택배사"
            contents={
              <Input
                type="text"
                size="small"
                placeholder="입력"
                maxWidth="300px"
                count
                maxLength={15}
                value={orderFilter.courier}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setOrderFilter((prev) => ({ ...prev, courier: e.target.value }))
                }
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setOrderFilter((prev) => ({ ...prev, courier: e.target.value.trim() }))
                }
              />
            }
            contentsClassName={styles['info']}
          />
          <FormField
            label="운송장번호"
            contents={
              <Input
                type="text"
                size="small"
                placeholder="최대 1개 입력"
                maxWidth="300px"
                count
                maxLength={20}
                value={orderFilter.invoiceNumber}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setOrderFilter((prev) => ({ ...prev, invoiceNumber: e.target.value }))
                }
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setOrderFilter((prev) => ({ ...prev, invoiceNumber: e.target.value.trim() }))
                }
              />
            }
            contentsClassName={styles['info']}
          />
        </>
      )}
      <Divider orientation="horizontal" color="gray100" />
      <FormField
        ref={(el) => {
          if (el) errorFieldRefs.current[`${orderData.id}.expectedDateTime`] = el;
        }}
        label="입고예정일시"
        required
        tooltip={{
          description: RECEIVE_TOOLTIP_MESSAGE.RECEIVE_EXPECTED_DATE,
        }}
        contents={
          <>
            <div className={styles['date-time-inputs']}>
              <DatePicker
                size="small"
                placeholder="날짜 선택"
                maxWidth="229px"
                portalTarget={document.body}
                shouldCloseOnNotInView
                placement={index + 1 === totalOrderCount ? 'top-left' : 'bottom-left'}
                disabled={!orderFilter.releaseCode}
                status={orderFilterState.expectedDate.status}
                validationMessage={orderFilterState.expectedDate.message}
                value={orderFilter.expectedDate}
                isDateDisabled={(date) => {
                  const formattedDate = formatDate(date, 'YYYY-MM-DD');
                  return formattedDate ? !operationDates.includes(formattedDate) : false;
                }}
                onChange={handleOperationDateChange}
                onFocus={() => setOrderFilterState((prev) => ({ ...prev, expectedDate: { status: 'default' } }))}
              />
              <Select
                size="small"
                placeholder="시간 선택"
                maxWidth="317px"
                dropdownPortalTarget={document.body}
                shouldCloseOnNotInView
                dropdownPlacement={index + 1 === totalOrderCount ? 'top-left' : 'bottom-left'}
                labelRenderer={(value) => {
                  const isDisabled = !orderFilter.expectedDate || isReleaseCodeParcel;
                  return renderOperationTimesSelect(value, isDisabled);
                }}
                dropdownRenderer={(option) => {
                  const isSelected = option.value === orderFilter.expectedTime?.value;
                  const isDisabled = !isSelected && option.disabled === true;
                  return renderOperationTimesDropdown(option, isDisabled, isSelected);
                }}
                disabled={!orderFilter.expectedDate || isReleaseCodeParcel}
                status={orderFilterState.expectedTime.status}
                validationMessage={orderFilterState.expectedTime.message}
                value={orderFilter.expectedTime}
                options={optionOperationTime}
                onChange={(
                  _: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
                  value: TypeSelectOption<string>
                ) => {
                  handleOperationTimeChange(value);
                }}
                onClick={() => setOrderFilterState((prev) => ({ ...prev, expectedTime: { status: 'default' } }))}
              />

              {lastFetchedOperationTimesTimestamp && (
                <div className={styles['timestamp-wrapper']}>
                  <TextButton
                    size="tiny"
                    weight="regular"
                    color="tertiary"
                    leftIcon={<RefreshIcon size={16} color="gray600" />}
                    onClick={refreshOperationDateTimes}
                  >
                    {formatDate(lastFetchedOperationTimesTimestamp, DATE_FORMAT.DATE_TIME)}
                  </TextButton>
                </div>
              )}
            </div>
            {isReleaseCodeParcel && (
              <InfoText>택배사 발송일이 아닌, 물류센터에 실제 도착하는 날짜를 선택 바랍니다.</InfoText>
            )}
          </>
        }
        contentsClassName={`${styles['info']} ${styles['expected-date']}`}
      />
    </div>
  );
};

export default ReceiveRegisterOrderInfo;
